import { Event } from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import { isDesktop } from "react-device-detect";
import { useEffect, useMemo, useState } from "react";
import { getCanonicalMediaUrls } from "@markit/common.utils";
import { firebaseAuth } from "../../../../firebase";
import { API_BASE } from "../../../../API";
import { isAndroidBrowser } from "../../../../utils/navigationUtils";
import { useTheme } from "../../../../hooks/useTheme";

type PDFViewerProps = {
  productData: Event;
  setProductData?: (productData: Event) => void;
  blurring?: boolean; // Whether to blur the pdf
};

// Helper component for displaying a PDF
const PDFViewer = (props: PDFViewerProps) => {
  const { productData, setProductData, blurring } = props;
  const { theme } = useTheme();

  const styles = {
    overlayButton: {
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 8,
      height: 35,
      width: 35,
      cursor: "pointer",
    },
  };

  const primaryMediaUrl = useMemo(
    () =>
      getCanonicalMediaUrls(productData).length > 0
        ? getCanonicalMediaUrls(productData)[0]
        : "",
    [productData]
  );

  // Shows the header toolbar if the mediaDownloadable is true and if not in creation mode
  const showHeaderToolbar = useMemo(
    () => productData.mediaDownloadable && !setProductData,
    [productData.mediaDownloadable, setProductData]
  );

  // Backend-streamed PDF URL (same origin pattern as other API_BASE downloads).
  // Hides the raw Firebase Storage URL; address bar shows the api function URL.
  const [proxyOpenUrl, setProxyOpenUrl] = useState<string>("");
  const shouldFetchProxyUrl =
    !isDesktop && showHeaderToolbar && !blurring && !!productData.id;

  useEffect(() => {
    if (!shouldFetchProxyUrl) {
      setProxyOpenUrl("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers: Record<string, string> = {};
        const currentUser = firebaseAuth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          headers.Authorization = `Bearer ${idToken}`;
        }
        const resp = await fetch(
          `${API_BASE}productMedia/token/${productData.id}`,
          { method: "POST", headers }
        );
        if (!resp.ok) return;
        const data = (await resp.json()) as { url?: string };
        if (!cancelled && data.url) {
          // Keep relative proxy paths on markitai.com so Firebase Hosting
          // rewrites can route them to the api function.
          setProxyOpenUrl(data.url);
        }
      } catch {
        // Leave proxyOpenUrl empty; we'll fall back to the raw URL.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldFetchProxyUrl, productData.id]);

  const pdfOpenHref = proxyOpenUrl || primaryMediaUrl;

  const showPhotoPreview = blurring && !!productData.photoURL;

  return (
    <div className="Centering" style={{ width: "100%" }}>
      <div
        className="Centering"
        style={{
          position: "relative",
          width: isDesktop ? 500 : "94vw",
          maxWidth: isDesktop ? 600 : "100%",
          height: "100%",
          aspectRatio: showPhotoPreview ? undefined : "3/4",
          borderRadius: 12,
          border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
          overflow: "hidden",
          backgroundColor:
            blurring && !showPhotoPreview
              ? Colors.WHITE1
              : theme.NewPrimaryBG.backgroundColor,
          filter:
            blurring && !showPhotoPreview
              ? "blur(6px) brightness(40%)"
              : "none",
          pointerEvents: blurring ? "none" : "auto",
          userSelect: blurring ? "none" : "auto",
        }}
      >
        {showPhotoPreview ? (
          <img
            src={productData.photoURL}
            alt="preview"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : isAndroidBrowser() ? (
          <div
            className="ColumnCentering"
            style={{
              width: "100%",
              height: "100%",
              padding: 24,
              gap: 14,
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <Icon icon="mdi:file-pdf-box" height={56} color={Colors.GRAY2} />
            <span
              className="bodyMedium"
              style={{ color: theme.PrimaryText.color }}
            >
              Inline PDF preview isn&apos;t supported.
              <br />
              Open the file to view it.
            </span>
            {pdfOpenHref ? (
              <a
                href={pdfOpenHref}
                target="_blank"
                rel="noopener noreferrer"
                className="bodyMedium"
                style={{
                  backgroundColor: Colors.WHITE,
                  border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                  borderRadius: 8,
                  padding: "10px 18px",
                  color: Colors.BLACK,
                  textDecoration: "none",
                }}
              >
                Open PDF
              </a>
            ) : null}
          </div>
        ) : (
          <>
            <object
              data={
                blurring
                  ? undefined
                  : primaryMediaUrl + (showHeaderToolbar ? "" : "#toolbar=0")
              }
              type="application/pdf"
              style={{ width: "100%", height: "100%" }}
            />
            {!isDesktop && !blurring && showHeaderToolbar ? (
              <div
                style={{
                  position: "absolute",
                  bottom: 20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                }}
              >
                <a
                  href={pdfOpenHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bodyMedium"
                  style={{
                    backgroundColor: Colors.WHITE1,
                    border: `0.5px solid ${Colors.GRAY2}`,
                    borderRadius: 8,
                    padding: "8px 14px",
                    color: Colors.BLACK,
                  }}
                >
                  Open in New Tab
                </a>
              </div>
            ) : null}
          </>
        )}
        {setProductData ? (
          <div
            style={{
              position: "absolute",
              top: showHeaderToolbar ? 66 : 10,
              right: 10,
            }}
          >
            <div
              className="Centering"
              style={{ ...styles.overlayButton }}
              onClick={() => {
                setProductData({
                  ...productData,
                  mediaURLs: [],
                  mediaURL: "", // for backsupport
                });
              }}
            >
              <Icon icon="ion:close" height={20} color={Colors.BLACK} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PDFViewer;
